import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import logoLight from '../assets/logo/icon-text-logo-light.svg';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');

  const forgotMutation = useMutation({
    mutationFn: async (emailAddress: string) => {
      const res = await api.post('/api/v1/auth/forgot-password', { email: emailAddress });
      if (!res.success) {
        throw new Error(res.error ?? 'Request failed');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    forgotMutation.mutate(email);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        <div className="flex justify-center mb-8">
          <img src={logoLight} alt="Halo" className="h-8" />
        </div>

        <div className="bg-gray-900 dark:bg-gray-950 rounded-2xl border border-gray-800 p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-bold text-white">Reset Password</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-primary/20 text-primary px-2 py-0.5 rounded-full">
              Admin
            </span>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Enter your email to receive a reset link</p>

          {forgotMutation.error && (
            <div className="px-3 py-2 mb-4 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
              {forgotMutation.error.message}
            </div>
          )}

          {forgotMutation.isSuccess ? (
            <div className="px-3 py-2 mb-4 text-sm text-green-400 bg-green-950/50 border border-green-900 rounded-lg">
              If an account exists with that email, a reset link has been sent.
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="admin@halo.com"
                  className="block w-full px-3 py-2.5 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={forgotMutation.isPending}
                className="w-full py-2.5 px-4 bg-primary text-gray-900 dark:text-gray-100 text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {forgotMutation.isPending ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-primary transition-colors"
            >
              Back to login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-600 dark:text-gray-300 mt-6">
          Halo Admin Panel &middot; Authorized personnel only
        </p>
      </div>
    </div>
  );
}
